/**
 * ENT-16247 — single source of truth for chat feedback reason chips.
 *
 * The canonical reason strings live in obtservices
 * (`POSITIVE_FEEDBACK_REASONS` / `NEGATIVE_FEEDBACK_REASONS`) so the persisted
 * `chat_feedback.reason` values stay identical across web (lu-frontend) and
 * mobile (new-ent-mobile-app). Both the message-bubble feedback
 * (ChatWidgetComponent) and the per-card feedback (ChatCardFeedbackComponent)
 * import from here instead of re-declaring the arrays.
 *
 * `localizeFeedbackReason()` provides a translated *display* label only — the
 * canonical English string remains the stored/submitted value so feedback
 * analytics are consistent regardless of the user's locale.
 */
import {
  POSITIVE_FEEDBACK_REASONS,
  NEGATIVE_FEEDBACK_REASONS,
  feedbackReasonsForRating
} from '@sabstravtech/obtservices/base';

export { POSITIVE_FEEDBACK_REASONS, NEGATIVE_FEEDBACK_REASONS, feedbackReasonsForRating };

/**
 * Maps a canonical feedback reason to its localised display label. Falls back
 * to the canonical string for any reason not yet translated.
 */
export function localizeFeedbackReason(reason: string): string {
  switch (reason) {
    case 'Answered my question':
      return $localize`:@@chatFeedbackReasonAnswered:Answered my question`;
    case 'Easy to understand':
      return $localize`:@@chatFeedbackReasonEasyToUnderstand:Easy to understand`;
    case 'Saved me time':
      return $localize`:@@chatFeedbackReasonSavedTime:Saved me time`;
    case 'Helpful detail':
      return $localize`:@@chatFeedbackReasonHelpfulDetail:Helpful detail`;
    case 'Incorrect answer':
      return $localize`:@@chatFeedbackReasonIncorrect:Incorrect answer`;
    case 'Did not understand my question':
      return $localize`:@@chatFeedbackReasonNotUnderstood:Did not understand my question`;
    case 'Not enough detail':
      return $localize`:@@chatFeedbackReasonNotEnoughDetail:Not enough detail`;
    case 'Too much detail':
      return $localize`:@@chatFeedbackReasonTooMuchDetail:Too much detail`;
    case 'Not relevant':
      return $localize`:@@chatFeedbackReasonNotRelevant:Not relevant`;
    default:
      return reason;
  }
}
